import {Helmet} from '@dr.pogodin/react-helmet';
import {DataStatus} from 'platform/components';
import {ZodSchema} from 'zod';

import {useCallback, useEffect, useRef, useState} from 'react';

import {omit} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  ConditionEnum,
  SaleVehicleAlbumPhotoResponseBody,
  useCloneSaleVehicleMutation,
  useCreateVehicleMutation,
  useGetActiveSaleVehicleQuery,
  useGetSaleVehicleAlbumPhotoListQuery,
  useGetVehicleQuery,
  useListSaleVehicleAlbumsByVehicleQuery,
  VehicleTypeEnum,
} from '@dms/api/vehicle';
import {
  SaleVehicle,
  SaleVehiclePhoto,
  SaleVehicleValidationSchema,
  SERVICE_INTERVALS_DEFAULT_VALUES,
  toAPIVehicle,
  toBaseVehicle,
  useAdditionalNote,
  useFormNavigate,
  useGetCarFeatures,
  useVehicleFormContext,
  useVehicleDocumentSync,
  VEHICLE_FORM_EVENTS,
  VehicleForm as VFKit,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {
  formatDocumentTitle,
  handleApiError,
  useMandatoryFields,
  useCloneCustomFields,
  FieldAvailabilityContext,
} from '@dms/shared';
import {AuditDataAssetsV2Body} from '@dms/teas';

import {useRequiredParams} from 'shared/web';

import {SaleVehicleForm} from '../components/SaleVehicleForm';
import {DEFAULT_VEHICLE_TYPE} from '../constants/vehicleConstants';
import {useConditionDataSubmit} from '../hooks/useConditionDataSubmit';
import {useCreatePurchaseVehicle} from '../hooks/useCreatePurchaseVehicle';

const PresetPhotosEffect = ({
  albumPhotos,
}: {
  albumPhotos: SaleVehicleAlbumPhotoResponseBody[] | undefined;
}) => {
  const {sendMachineEvent, globalState} = useVehicleFormContext();
  const formReady = Boolean(globalState.context.form);
  useEffect(() => {
    if (!formReady || !albumPhotos || albumPhotos.length === 0) {
      return;
    }
    const mapped: Array<SaleVehiclePhoto> = albumPhotos.map((p) => ({
      fileId: p.fileId,
      fileUri: p.url,
    }));

    sendMachineEvent({type: VEHICLE_FORM_EVENTS.PHOTOS_PRESET_SUCCESS, photos: mapped});
  }, [albumPhotos, sendMachineEvent, formReady]);
  return null;
};

export function CloneSaleVehicle() {
  const {navigateOnConfirm, navigateOnDiscard, isBusinessCaseReferrer} = useFormNavigate();
  const {id: vehicleId} = useRequiredParams();

  const [
    cloneCustomFields,
    {isLoading: isLoadingVehicleCustomFields, isError: isErrorVehicleCustomFields},
  ] = useCloneCustomFields(vehicleId);

  const {
    generateSchema,
    isMandatoryFieldsEnabled,
    isLoading: isMandatoryFieldsLoading,
  } = useMandatoryFields();

  const {
    data: vehicle,
    isLoading: isLoadingVehicle,
    isError: isErrorVehicle,
  } = useGetVehicleQuery(
    {vehicleId},
    {
      skip: isNilOrEmpty(vehicleId),
    }
  );

  const {
    isLoading: isSaleVehicleLoading,
    salePresetId,
    vehicleSourceCodeId,
  } = useGetActiveSaleVehicleQuery(
    {vehicleId},
    {
      skip: isNilOrEmpty(vehicleId),
      selectFromResult: ({data, isLoading}) => ({
        isLoading,
        vehicleSourceCodeId: data?.vehicleSourceCodeId,
        salePresetId: data?.salePresetId,
        saleVehicleId: data?.id,
      }),
    }
  );

  const [cloneSaleVehicleMutation] = useCloneSaleVehicleMutation();

  const {saleVehicleAlbum, isLoading: isLoadingSaleVehicleAlbum} =
    useListSaleVehicleAlbumsByVehicleQuery(
      {vehicleId},
      {
        skip: isNilOrEmpty(vehicleId),
        selectFromResult: ({data, isLoading}) => ({
          isLoading,
          data,
          saleVehicleAlbum: data?.find(
            (album) => album.active && album.name === 'PHOTOS_CAR_SALES'
          ),
        }),
      }
    );

  const {data: saleVehicleAlbumPhotos} = useGetSaleVehicleAlbumPhotoListQuery(
    {
      saleVehicleId: saleVehicleAlbum?.saleVehicleId ?? '',
      saleVehicleAlbumId: saleVehicleAlbum?.id ?? '',
    },
    {
      skip: isNilOrEmpty(saleVehicleAlbum),
    }
  );

  const currentContextRef = useRef<FieldAvailabilityContext>({
    vehicleType: DEFAULT_VEHICLE_TYPE,
  });

  const [formSchema, setFormSchema] = useState<ZodSchema<SaleVehicle>>(() =>
    generateSchema(SaleVehicleValidationSchema, currentContextRef.current)
  );

  useEffect(() => {
    if (vehicle) {
      currentContextRef.current = {
        ...currentContextRef.current,
        vehicleType: (vehicle.type as VehicleTypeEnum) ?? DEFAULT_VEHICLE_TYPE,
      };
      setFormSchema(
        generateSchema(
          SaleVehicleValidationSchema,
          currentContextRef.current,
          (vehicle.condition as ConditionEnum) ?? undefined
        )
      );
    }
  }, [vehicle?.type, vehicle?.condition, generateSchema]);

  const [getCarFeatures] = useGetCarFeatures();

  const [createVehicleMutation, {isLoading: isCreating}] = useCreateVehicleMutation();

  const {handleSubmit: handleConditionDataSubmit} = useConditionDataSubmit();
  const {attachToBusinessCase} = useCreatePurchaseVehicle();

  const {handleSaveNote} = useAdditionalNote();
  const {syncDocuments} = useVehicleDocumentSync();

  const handleVehicleTypeChange = useCallback(
    (type: VehicleTypeEnum, condition?: ConditionEnum) => {
      currentContextRef.current = {...currentContextRef.current, vehicleType: type};
      const newSchema = generateSchema(
        SaleVehicleValidationSchema,
        currentContextRef.current,
        condition
      );
      setFormSchema(newSchema);
    },
    [generateSchema]
  );

  const handleFuelTypeChange = useCallback(
    (fuelTypeCondition: FieldAvailabilityContext) => {
      currentContextRef.current = {...currentContextRef.current, ...fuelTypeCondition};
      const newSchema = generateSchema(SaleVehicleValidationSchema, currentContextRef.current);
      setFormSchema(newSchema);
    },
    [generateSchema]
  );

  const handleSave = async (values: SaleVehicle) => {
    const sourcingData = await getCarFeatures(values);
    const apiPayload = toAPIVehicle(values, null, sourcingData);

    const onCreate = () =>
      createVehicleMutation({
        createVehicleRequestBody: apiPayload,
        validateMandatoryFields: isMandatoryFieldsEnabled(values.condition?.[0] ?? undefined),
      })
        .unwrap()
        .then((createdVehicle) => createdVehicle.id)
        // Clone sale vehicle from source (copies prices/config + photos/preset/source code)
        .then(async (id) => {
          await cloneSaleVehicleMutation({
            vehicleId,
            cloneSaleVehicleRequestBody: {
              toVehicleId: id,
              salePresetId: values.salePresetId,
              vehicleSourceCodeId: values.vehicleSourceCodeId,
              photos:
                values.photos?.map(({fileId}) => ({
                  fileId,
                })) ?? [],
              targetAlbum: values.photos?.length
                ? ((values.targetAlbum?.at(0) as 'PHOTOS_CAR_SALES' | 'PHOTOS_CAR_PURCHASE') ??
                  'PHOTOS_CAR_PURCHASE')
                : null,
            },
          }).unwrap();
          return id;
        })
        // Handle additional note
        .then(async (id) => {
          if (values.additionalNote) {
            await handleSaveNote(id, values.additionalNote);
          }
          return id;
        })
        // Clone custom fields
        .then(cloneCustomFields)
        // Handle condition data
        .then(async (id) => {
          await handleConditionDataSubmit(
            id,
            omit(['sessionId'], values.auditData ?? {}) ?? {},
            (values.auditAssets as Record<string, AuditDataAssetsV2Body>) ?? {}
          );
          return id;
        })
        // Sync battery certificate document
        .then(async (id) => {
          await syncDocuments({
            vehicleId: id,
            contextTarget: 'vehicle',
            fileFields: [
              {
                fieldValue: values.batteryCertificateDocument,
                documentNote: i18n.t('entity.vehicle.labels.batteryCertificate'),
              },
            ],
          });
          return id;
        })
        .then(async (id) => {
          if (isBusinessCaseReferrer) {
            await attachToBusinessCase(id);
          }
          navigateOnConfirm(id);
        });

    // eslint-disable-next-line no-restricted-syntax
    return onCreate().catch(handleApiError);
  };

  const isLoading =
    isMandatoryFieldsLoading ||
    isLoadingVehicle ||
    isSaleVehicleLoading ||
    isLoadingSaleVehicleAlbum ||
    isLoadingVehicleCustomFields;
  const isError = isErrorVehicle || isNilOrEmpty(vehicleId) || isErrorVehicleCustomFields;

  return (
    <>
      <Helmet>
        <title>
          {formatDocumentTitle(
            i18n.t('entity.vehicle.labels.clone'),
            i18n.t('entity.vehicle.labels.pageTitle')
          )}
        </title>
      </Helmet>
      <DataStatus isLoading={isLoading} isError={isError}>
        <VFKit.Form
          showMandatoryFields
          experimentalZodSchema={formSchema}
          defaultValues={{
            ...toBaseVehicle(omit(['registrationPlate', 'vin'], vehicle!)),
            targetAlbum: ['PHOTOS_CAR_SALES'],
            serviceIntervals: {
              ...SERVICE_INTERVALS_DEFAULT_VALUES,
              ...vehicle?.serviceIntervals,
            },
            salePresetId,
            vehicleSourceCodeId,
          }}
        >
          {saleVehicleAlbumPhotos && saleVehicleAlbumPhotos.length > 0 && (
            <PresetPhotosEffect albumPhotos={saleVehicleAlbumPhotos ?? []} />
          )}
          <SaleVehicleForm
            onSave={handleSave}
            onVehicleTypeChange={handleVehicleTypeChange}
            onFuelTypeChange={handleFuelTypeChange}
            onDiscard={navigateOnDiscard}
            isCreating={isCreating}
            vehicleId={vehicleId}
            tabRoutePattern="cloneV2Tab"
            modalKeys={{
              headline: i18n.t('entity.vehicle.labels.clone'),
              create: i18n.t('entity.vehicle.labels.clone'),
            }}
          />
        </VFKit.Form>
      </DataStatus>
    </>
  );
}
